/**
 * ReviewerMode — Independent Reviewer Mode (read-only).
 *
 * Consolidates every defensibility artefact for a run into one auditor-facing
 * view: a single-line verdict, the evidence-weighted confidence band, coverage &
 * calibration map, external-standards crosswalk, human challenge log, empirical
 * validation ledger, deliverable-QA status and the assurance gate. No writes.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Scale, RefreshCw, ShieldCheck, ShieldAlert, CheckCircle2, AlertTriangle, Link2, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import ConfidenceBand from "../ConfidenceBand";

const VERDICT_TONE = {
  strong: { cls: "bg-emerald-50 border-emerald-300 text-emerald-900", Icon: CheckCircle2 },
  measured: { cls: "bg-amber-50 border-amber-300 text-amber-900", Icon: AlertTriangle },
  caution: { cls: "bg-red-50 border-red-300 text-red-900", Icon: ShieldAlert },
};
const STATUS_PILL = {
  met: "bg-emerald-100 text-emerald-800", partial: "bg-amber-100 text-amber-800",
  gap: "bg-red-100 text-red-800", claimed_unproven: "bg-purple-100 text-purple-800",
};
const XLABEL = { met: "Met", partial: "Partial", gap: "Gap", claimed_unproven: "Claimed — self-declared" };

const fmtWhen = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const Section = ({ title, children, testid }) => (
  <section className="ek-surface border ek-line p-5" data-testid={testid}>
    <h3 className="text-[12px] font-medium tracking-[0.16em] ek-text-muted uppercase mb-3">{title}</h3>
    {children}
  </section>
);

export default function ReviewerMode({ runId, summary: preloaded }) {
  const [data, setData] = useState(preloaded || null);
  const [loading, setLoading] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [reviewerUrl, setReviewerUrl] = useState(null);
  const [cobrand, setCobrand] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ recipient_email: "", recipient_name: "", message: "" });
  const [links, setLinks] = useState([]);

  const loadLinks = useCallback(async () => {
    if (!runId) return;
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/shares`);
      setLinks((data.items || []).filter((s) => s.scope === "reviewer"));
    } catch { /* non-blocking */ }
  }, [runId]);

  const load = useCallback(async () => {
    if (preloaded || !runId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/reviewer-summary`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load reviewer summary");
    } finally { setLoading(false); }
  }, [runId, preloaded]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLinks(); }, [loadLinks]);

  const revokeLink = async (token) => {
    try {
      await api.delete(`/admin/assessments/shares/${token}`);
      toast.success("Auditor link revoked");
      loadLinks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revoke");
    }
  };

  const mintAuditorLink = async () => {
    setLinkBusy(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/reviewer-link`, { expires_in_days: 30, cobrand });
      setReviewerUrl(data.reviewer_url);
      try { await navigator.clipboard.writeText(data.reviewer_url); toast.success("Auditor link copied to clipboard"); }
      catch { toast.success("Auditor link created"); }
      loadLinks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create auditor link");
    } finally { setLinkBusy(false); }
  };

  const emailAuditorLink = async () => {
    if (!emailForm.recipient_email.trim()) { toast.error("Enter the auditor's email"); return; }
    setLinkBusy(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/reviewer-link-email`, { ...emailForm, expires_in_days: 30, cobrand });
      setReviewerUrl(data.reviewer_url);
      toast[data.email_sent ? "success" : "warning"](data.email_sent ? "Auditor link emailed" : "Link created (email not configured)");
      setEmailOpen(false);
      setEmailForm({ recipient_email: "", recipient_name: "", message: "" });
      loadLinks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not email auditor link");
    } finally { setLinkBusy(false); }
  };

  if (!data) {
    return (
      <div className="text-[13px] ek-text-muted p-5" data-testid="reviewer-loading">
        {loading ? "Assembling reviewer view…" : "No reviewer summary available."}
      </div>
    );
  }

  const v = data.verdict || {};
  const tone = VERDICT_TONE[v.tone] || VERDICT_TONE.measured;
  const cm = data.coverage_map;
  const cx = data.crosswalk;
  const log = data.challenge_log;
  const led = data.validation_ledger;

  return (
    <div className="space-y-4" data-testid="reviewer-mode">
      {/* Header + verdict */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 ek-text-soft text-[12px] font-medium uppercase tracking-[0.16em]">
          <Scale size={14} /> Independent reviewer mode · read-only
        </div>
        <button type="button" onClick={load} disabled={loading}
          className="ek-text-muted hover:text-[#7737F6] disabled:opacity-50" title="Refresh" data-testid="reviewer-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className={`border p-4 ${tone.cls}`} data-testid="reviewer-verdict">
        <div className="flex items-start gap-2">
          <tone.Icon size={18} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] opacity-70 mb-0.5">Reviewer verdict</div>
            <div className="text-[14px] font-medium leading-relaxed">{v.headline}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
          <span className="px-2 py-0.5 rounded-full bg-white/60 border border-black/10" data-testid="reviewer-sig-confidence">Confidence: {v.signals?.confidence_label}</span>
          <span className="px-2 py-0.5 rounded-full bg-white/60 border border-black/10">Verified: {v.signals?.verified_pct}%</span>
          <span className="px-2 py-0.5 rounded-full bg-white/60 border border-black/10">Claimed-unproven: {v.signals?.claimed_unproven}</span>
          <span className="px-2 py-0.5 rounded-full bg-white/60 border border-black/10">Challenges: {v.signals?.challenges_logged}</span>
          <span className="px-2 py-0.5 rounded-full bg-white/60 border border-black/10">QA: {v.signals?.qa_status}</span>
          <span className="px-2 py-0.5 rounded-full bg-white/60 border border-black/10 inline-flex items-center gap-1">
            <ShieldCheck size={11} /> Assurance: {v.signals?.assurance_approved ? "approved" : "not approved"}
          </span>
        </div>
      </div>

      <div className="text-[13px] ek-text-muted">
        <strong className="ek-text">{data.client}</strong> · {data.sub_brand} · Overall{" "}
        <strong className="ek-text">{data.overall}/5</strong> {data.level ? `· ${data.level}` : ""}{" "}
        {data.finalised ? "· finalised" : "· draft"}
      </div>

      {runId && (
        <div className="ek-surface border ek-line p-4" data-testid="reviewer-auditor-link">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[12px] ek-text-muted">
              <strong className="ek-text">Share with an auditor.</strong> Create a no-login, read-only link to this reviewer view for the client's risk / audit function.
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={mintAuditorLink} disabled={linkBusy}
                className="ek-btn ek-btn-outline text-[13px] inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="reviewer-create-link-btn">
                <Link2 size={14} /> {linkBusy ? "Working…" : "Copy link"}
              </button>
              <button type="button" onClick={() => setEmailOpen((v) => !v)} disabled={linkBusy}
                className="ek-btn ek-btn-primary text-[13px] inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="reviewer-email-toggle-btn">
                <Mail size={14} /> Email link
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 mt-3 text-[12px] ek-text-muted cursor-pointer" data-testid="reviewer-cobrand-toggle">
            <input type="checkbox" checked={cobrand} onChange={(e) => setCobrand(e.target.checked)} className="accent-[#7737F6]" />
            Co-brand the auditor page with the client logo (when one is set)
          </label>

          {emailOpen && (
            <div className="mt-3 border-t ek-line pt-3 space-y-2" data-testid="reviewer-email-form">
              <div className="flex gap-2">
                <input type="email" value={emailForm.recipient_email} onChange={(e) => setEmailForm((f) => ({ ...f, recipient_email: e.target.value }))}
                  placeholder="auditor@client.com" className="flex-1 border ek-line px-3 py-2 text-[13px]" data-testid="reviewer-email-input" />
                <input value={emailForm.recipient_name} onChange={(e) => setEmailForm((f) => ({ ...f, recipient_name: e.target.value }))}
                  placeholder="Name (optional)" className="w-40 border ek-line px-3 py-2 text-[13px]" data-testid="reviewer-email-name" />
              </div>
              <textarea value={emailForm.message} onChange={(e) => setEmailForm((f) => ({ ...f, message: e.target.value }))} rows={2}
                placeholder="Short cover note (optional)…" className="w-full border ek-line px-3 py-2 text-[13px]" data-testid="reviewer-email-message" />
              <button type="button" onClick={emailAuditorLink} disabled={linkBusy}
                className="ek-btn ek-btn-primary text-[13px] inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="reviewer-email-send-btn">
                <Mail size={14} /> {linkBusy ? "Sending…" : "Send auditor link"}
              </button>
            </div>
          )}

          {reviewerUrl && (
            <input readOnly value={reviewerUrl} onFocus={(e) => e.target.select()}
              className="w-full mt-2 border ek-line px-3 py-2 text-[12px] ek-text-muted" data-testid="reviewer-link-url" />
          )}

          {links.length > 0 && (
            <div className="mt-4 border-t ek-line pt-3" data-testid="reviewer-access-log">
              <div className="text-[11px] font-medium tracking-[0.14em] ek-text-muted uppercase mb-2">Auditor access log</div>
              <ul className="space-y-1.5">
                {links.map((s) => (
                  <li key={s.token} className="flex items-center justify-between gap-2 text-[12px]" data-testid={`reviewer-link-row-${s.token}`}>
                    <div className="min-w-0">
                      <span className="ek-text">{s.recipient_email || s.recipient_name || "Link"}</span>
                      <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${s.revoked ? "ek-surface-2 ek-text-muted" : (s.view_count > 0 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")}`}>
                        {s.revoked ? "Revoked" : s.view_count > 0 ? `Opened ${s.view_count}×` : "Not opened"}
                      </span>
                      <div className="text-[10px] ek-text-muted">
                        {s.last_viewed_at ? `Last opened ${fmtWhen(s.last_viewed_at)}` : `Created ${fmtWhen(s.created_at)}`}
                        {s.expires_at ? ` · expires ${fmtWhen(s.expires_at)}` : ""}
                      </div>
                    </div>
                    {!s.revoked && (
                      <button type="button" onClick={() => revokeLink(s.token)}
                        className="ek-text-muted hover:text-red-600 text-[11px]" data-testid={`reviewer-link-revoke-${s.token}`}>Revoke</button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Confidence band */}
      {data.confidence && (
        <Section title="Evidence-weighted confidence" testid="reviewer-confidence">
          <ConfidenceBand confidence={data.confidence} sensitivity={data.sensitivity} selfDeclared={false} />
        </Section>
      )}

      {/* Coverage & calibration map */}
      {cm?.rows?.length > 0 && (
        <Section title="Coverage & calibration map" testid="reviewer-coverage">
          <p className="text-[12px] ek-text-muted mb-2">
            Overall <strong>{cm.overall_coverage_pct}%</strong> evidence coverage · <strong>{cm.overall_verified_pct}%</strong> verified · {cm.confidence_label} confidence.
          </p>
          <table className="w-full text-[12px]">
            <thead><tr className="text-left ek-text-muted border-b ek-line">
              <th className="py-1">Dimension</th><th className="py-1 text-center">Maturity</th>
              <th className="py-1 text-center">Coverage</th><th className="py-1 text-center">Verified</th><th className="py-1">Status</th>
            </tr></thead>
            <tbody>
              {cm.rows.map((r) => (
                <tr key={r.dimension_id} className="border-b ek-line">
                  <td className="py-1.5">{r.name}</td>
                  <td className="py-1.5 text-center">{r.maturity != null ? `${r.maturity}/${cm.scale_max}` : "—"}</td>
                  <td className="py-1.5 text-center">{r.coverage_pct}%</td>
                  <td className="py-1.5 text-center">{r.verified_pct}%</td>
                  <td className="py-1.5"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL[r.status]}`}>{XLABEL[r.status]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Standards crosswalk */}
      {cx?.standards?.length > 0 && (
        <Section title="External standards crosswalk" testid="reviewer-crosswalk">
          {cx.claimed_unproven_count > 0 && (
            <p className="text-[12px] text-purple-800 bg-purple-50 border border-purple-200 p-2 mb-2">
              <strong>{cx.claimed_unproven_count}</strong> clause(s) claimed but self-declared — evidence before relying on for assurance.
            </p>
          )}
          {cx.standards.map((s) => (
            <div key={s.id} className="mb-3">
              <div className="text-[12px] font-medium ek-text mb-1">{s.name} — {s.coverage_pct}% mapped</div>
              <div className="flex flex-wrap gap-1">
                {s.clauses.map((c) => (
                  <span key={c.ref} className={`text-[10px] px-2 py-0.5 rounded ${STATUS_PILL[c.status]}`} title={`${c.title} · ${c.mapped.join(", ") || "no mapped dimension"}`}>
                    {c.ref}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Human challenge log */}
      <Section title="Human challenge log" testid="reviewer-challenges">
        <p className="text-[12px] ek-text-muted mb-2">{log?.summary}</p>
        {log?.entries?.length > 0 && (
          <ul className="space-y-1.5">
            {log.entries.map((e) => (
              <li key={e.id} className="text-[12px] ek-text-soft">
                <strong className="capitalize">{e.action}</strong> · {e.claim}
                {e.rationale && <span className="ek-text-muted italic"> — {e.rationale}</span>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Empirical validation ledger */}
      <Section title="Empirical validation ledger" testid="reviewer-ledger">
        <p className="text-[12px] ek-text-muted mb-2">{led?.summary}</p>
        {led?.status === "measured" && led.rows?.length > 0 && (
          <table className="w-full text-[12px]">
            <thead><tr className="text-left ek-text-muted border-b ek-line">
              <th className="py-1">Dimension</th><th className="py-1 text-center">Predicted</th>
              <th className="py-1 text-center">Actual</th><th className="py-1 text-center">Error</th>
            </tr></thead>
            <tbody>
              {led.rows.map((r) => (
                <tr key={r.dimension_id} className="border-b ek-line">
                  <td className="py-1.5">{r.name}</td>
                  <td className="py-1.5 text-center">{r.predicted}</td>
                  <td className="py-1.5 text-center font-medium">{r.actual}</td>
                  <td className={`py-1.5 text-center font-semibold ${r.within_tolerance ? "text-emerald-700" : "text-amber-700"}`}>
                    {r.error >= 0 ? "+" : ""}{r.error}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <p className="text-[11px] ek-text-muted">
        Scoring-engine model card v{data.model_card?.version} · deterministic confidence bands (no AI) ·
        weights: asserted {data.model_card?.tier_weights?.asserted} · documented {data.model_card?.tier_weights?.documented} · verified {data.model_card?.tier_weights?.verified}.
      </p>
    </div>
  );
}
